import { getSignInsForDate,getVisitorsForLocation, formatTime, getLocationVisitCounts, getLeaderboardData,formatDuration } from '../utils/commonFunctions';
import React from 'react';
import { LOCATION_ICONS, COLOR_PALETTE,availableLocations, LOCATIONS } from '../utils/constants';

import '../css/calendar.css';
import '../css/common.css';
import '../css/dashboard.css';
import '../css/employee-portal.css';
import '../css/kiosk.css';
import { EmployeeSignIns, EventData } from './Shared/Types';

interface CalendarHeaderProps {
    currentDate: Date;
    prevMonth: () => void;
    nextMonth: () => void;
    handleMonthChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
    handleYearChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
    monthOptions: string[];
    yearOptions: number[];
}

interface CalendarGridProps {
    currentDate: Date;
    employees: any[]; // Replace with more specific type if available
    onDateClick: (date: Date) => void;
}

interface LocationDetailViewProps {
    location: string;
    employees: any[]; // Replace with more specific type if available
    timeFilter: string;
    setTimeFilter: (filter: string) => void;
    onBack: () => void;
    viewUserProfile: (employee: any) => void; // Replace with more specific type if available
}

interface LocationsGridViewProps {
    employees: any[]; // Replace with more specific type if available
    timeFilter: string;
    setTimeFilter: (filter: string) => void;
    onSelectLocation: (location: string) => void;
    searchQuery: string;
    setSearchQuery: (query: string) => void;
}

export const CalendarHeader = ({
    currentDate,
    prevMonth,
    nextMonth,
    handleMonthChange,
    handleYearChange,
    monthOptions,
    yearOptions
  }:CalendarHeaderProps) => (
    <header className="calendar-header">
      <h1 className="calendar-title">Magni Sign in/out Calendar</h1>
      <div className="calendar-navigation">
        <button className="nav-button" onClick={prevMonth}>{'< Prev'}</button>
        <span className="month-year">{monthOptions[currentDate.getMonth()]} {currentDate.getFullYear()}</span>
        <button className="nav-button" onClick={nextMonth}>{'Next >'}</button>
      </div>
      <div className="dropdowns">
        <select value={currentDate.getMonth()} onChange={handleMonthChange}>
          {monthOptions.map((m, i) => <option key={i} value={i}>{m}</option>)}
        </select>
        <select value={currentDate.getFullYear()} onChange={handleYearChange}>
          {yearOptions.map((y, i) => <option key={i} value={y}>{y}</option>)}
        </select>
      </div>
    </header>
  );

export const CalendarGrid = ({ currentDate, employees, onDateClick }:CalendarGridProps) => {
  const daysOfWeek = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];
  const firstDayOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
  const firstDayOfWeek = firstDayOfMonth.getDay();
  const lastDayOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth()+1, 0);
  const daysInMonth = lastDayOfMonth.getDate();
  const daysFromPrevMonth = firstDayOfWeek;
  const prevMonthLastDay = new Date(currentDate.getFullYear(), currentDate.getMonth(), 0).getDate();
  const totalCells = Math.ceil((daysFromPrevMonth + daysInMonth)/7)*7;
  const daysFromNextMonth = totalCells - daysFromPrevMonth - daysInMonth;
  const today = new Date();
  const calendarCells = [];

  // Prev month
  for (let i = 0; i < daysFromPrevMonth; i++) {
    const day = prevMonthLastDay - daysFromPrevMonth + i + 1;
    const date = new Date(currentDate.getFullYear(), currentDate.getMonth()-1, day);
    const signIns = getSignInsForDate(employees, date);
    calendarCells.push({
      day, date, isCurrentMonth: false, isToday:false,
      signInCount: signIns.length
    });
  }
  // Current month
  for (let i = 1; i <= daysInMonth; i++) {
    const date = new Date(currentDate.getFullYear(), currentDate.getMonth(), i);
    const isToday = (today.getDate()===i && today.getMonth()===currentDate.getMonth() && today.getFullYear()===currentDate.getFullYear());
    const signIns = getSignInsForDate(employees, date);
    calendarCells.push({
      day: i, date, isCurrentMonth:true, isToday,
      signInCount: signIns.length
    });
  }
  // Next month
  for (let i = 1; i <= daysFromNextMonth; i++) {
    const date = new Date(currentDate.getFullYear(), currentDate.getMonth()+1, i);
    const signIns = getSignInsForDate(employees, date);
    calendarCells.push({
      day: i, date, isCurrentMonth:false, isToday:false,
      signInCount: signIns.length
    });
  }

  return (
    <div className="calendar-grid">
      {daysOfWeek.map(d => <div key={d} className="day-header">{d}</div>)}
      {calendarCells.map((cell, idx) => (
        <div
          key={idx}
          className={`day-cell ${!cell.isCurrentMonth?'other-month':''} ${cell.isToday?'today':''}`}
          onClick={(e)=> {
            e.preventDefault();
            // Only trigger date click if the date is actually clicked by the user
            // This prevents auto-selection when component mounts
            if (e.isTrusted) {
              onDateClick(cell.date);
            }
            // Note: If the issue persists on page refresh, ensure that the parent component
            // initializes selectedDate as null and doesn't set it programmatically during mount
          }}
        >
          <div className="day-number">{cell.day}</div>
          {cell.signInCount>0 && (
            <div className="day-count">{cell.signInCount}</div>
          )}
        </div>
      ))}
    </div>
  );
};

export const LocationDetailView = ({ 
  location, 
  employees, 
  timeFilter, 
  setTimeFilter,
  onBack,
  viewUserProfile
}:LocationDetailViewProps) => {
  // Add pagination state
  const [currentPage, setCurrentPage] = React.useState(1);
  const visitorsPerPage = 5; // Number of visitors to show per page
  
  // Get visitors for this location and sort them chronologically
  const unsortedVisitors = getVisitorsForLocation(employees, location, timeFilter);
  
  // Sort visitors chronologically based on their first visit date
  const visitors = React.useMemo(() => {
    return [...unsortedVisitors].sort((a, b) => {
      return a.signIns[0].eventDate.getTime() - b.signIns[0].eventDate.getTime();
    });
  }, [unsortedVisitors]);
  
  // Calculate pagination values
  const totalVisitors = visitors.length;
  const totalPages = Math.max(1, Math.ceil(totalVisitors / visitorsPerPage));
  
  
  // Get visitors for current page
  const startIndex = (currentPage - 1) * visitorsPerPage;
  const endIndex = Math.min(startIndex + visitorsPerPage, totalVisitors);
  const currentVisitors = visitors.slice(startIndex, endIndex);
  
  
  // Format a date based on the current time filter
  const formatDateForTimeFilter = (date:Date| null) => {

    if (!date) return '';

    if (timeFilter === 'day') {
      return formatTime(date);
    } else if (timeFilter === 'week') {
      const weekdays = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      return weekdays[date.getDay()];
    } else if (timeFilter === 'month') {
      const months = ['January', 'February', 'March', 'April', 'May', 'June', 
                      'July', 'August', 'September', 'October', 'November', 'December'];
      return months[date.getMonth()].substring(0, 3) + ' ' + date.getDate();
    } else if (timeFilter === 'year') {
      const months = ['January', 'February', 'March', 'April', 'May', 'June', 
                     'July', 'August', 'September', 'October', 'November', 'December'];
      return months[date.getMonth()];
    }
    return '';
  };
  
  // Get time period text
  const getTimePeriodText = () => {
    switch(timeFilter) {
      case 'day': return 'Today';
      case 'week': return 'This Week';
      case 'month': return 'This Month';
      case 'year': return 'This Year';
      default: return '';
    };
  };
  
  // Get date icon based on time filter
  const getDateIcon = () => {
    if (timeFilter === 'day') {
      return '🕒';
    } else if (timeFilter === 'week') {
      return '📅';
    } else if (timeFilter === 'month' || timeFilter === 'year') {
      return '📆';
    }
    return '📅';
  };
  
  return (
    <div className="location-detail-container">
      <div className="back-to-locations" onClick={onBack}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M15.41 7.41L14 6L8 12L14 18L15.41 16.59L10.83 12L15.41 7.41Z" fill="currentColor"/>
        </svg>
        Back to Locations
      </div>
      
      <div className="location-detail-header">
        <div className="location-detail-emoji">
          {LOCATION_ICONS[location]}
        </div>
        <div className="location-detail-info">
          <h2 className="location-detail-title">{location} Office</h2>
          <div className="location-detail-stats">
            <div className="location-stat">
              {visitors.length} visitor{visitors.length !== 1 ? 's' : ''} {getTimePeriodText().toLowerCase()}
            </div>
            <div className="location-stat">
              {visitors.filter(v => v.type === 'Magni').length} Magni staff
            </div>
            <div className="location-stat">
              {visitors.filter(v => v.type === 'Guest').length} Guest{visitors.filter(v => v.type === 'Guest').length !== 1 ? 's' : ''}
            </div>
          </div>
        </div>
      </div>
      
      <div className="date-filters-apple">
        <button 
          className={`date-filter-apple ${timeFilter === 'day' ? 'active' : ''}`}
          onClick={() => {setTimeFilter('day'); setCurrentPage(1);}}
        >
          Day
        </button>
        <button 
          className={`date-filter-apple ${timeFilter === 'week' ? 'active' : ''}`}
          onClick={() => {setTimeFilter('week'); setCurrentPage(1);}}
        >
          Week
        </button>
        <button 
          className={`date-filter-apple ${timeFilter === 'month' ? 'active' : ''}`}
          onClick={() => {setTimeFilter('month'); setCurrentPage(1);}}
        >
          Month
        </button>
        <button 
          className={`date-filter-apple ${timeFilter === 'year' ? 'active' : ''}`}
          onClick={() => {setTimeFilter('year'); setCurrentPage(1);}}
        >
          Year
        </button>
      </div>
      
      <h3 className="section-title-apple" style={{ marginTop: '20px' }}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
             xmlns="http://www.w3.org/2000/svg" className="section-icon-apple">
          <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" fill="currentColor"/>
        </svg>
        Visitors {getTimePeriodText()} {totalVisitors > 0 && `(${startIndex + 1}-${endIndex} of ${totalVisitors})`}
      </h3>
      
      {totalVisitors === 0 ? (
        <div className="empty-state-container">
          <div className="empty-state-icon">👀</div>
          <div className="empty-state-message">No visitors {getTimePeriodText().toLowerCase()}</div>
          <div className="empty-state-submessage">Try a different time period or check back later</div>
        </div>
      ) : (
        <>
          <div className="employee-list-apple">
            {currentVisitors.map((visitor:EmployeeSignIns, index:number) => {
              const colors = COLOR_PALETTE[index % COLOR_PALETTE.length];
              const visitDate = visitor.signIns[0]?.eventDate; // Get first visit date
              
              return (
                <div className="visitor-card-container" key={visitor.id}>
                  <div 
                    className="employee-card-apple employee-card-visible"
                    onClick={() => viewUserProfile(visitor.name)}
                  >
                    {/* Date indicator */}
                    <div className="visitor-date-indicator">
                      <span>{getDateIcon()}</span>
                      <span>{formatDateForTimeFilter(visitDate)}</span>
                    </div>
                    
                    <div className="employee-card-header-apple">
                      <div 
                        className="employee-initial-apple" 
                        style={{ backgroundColor: colors.bg, color: colors.initial }}
                      >
                        {visitor.name.charAt(0)}
                      </div>
                      
                      <div className="employee-info-apple">
                        <div className="employee-name-apple">{visitor.name}</div>
                        <div 
                          className={`employee-type-apple ${visitor.type.toLowerCase()}`}
                        >
                          {visitor.type}
                        </div>
                      </div>
                    </div>
                    
                    <div className="employee-timeline-apple">
                      <div className="time-entries-apple">
                        {visitor.signIns.map((visit:EventData, visitIndex:any) => (
                          <div 
                            key={visitIndex} 
                            className="time-entry-apple in"
                          >
                            <span className="time-value-apple">
                              {formatTime(visit.eventDate)}
                            </span>
                            <span className="action-label-apple">
                              Signed In at {location}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    <div className="card-footer-apple">
                      <div className="view-profile-button-apple">
                        <span>View Profile</span>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M8.59 16.59L13.17 12L8.59 7.41L10 6L16 12L10 18L8.59 16.59Z" fill="currentColor"/>
                        </svg>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          
          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="pagination-container">
              <button 
                className="pagination-button" 
                //onClick={handlePrevPage}
                disabled={currentPage === 1}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M15.41 7.41L14 6L8 12L14 18L15.41 16.59L10.83 12L15.41 7.41Z" fill="currentColor"/>
                </svg>
                Previous
              </button>
              
              <span className="pagination-info">
                Page {currentPage} of {totalPages}
              </span>
              
              <button 
                className="pagination-button" 
                //onClick={handleNextPage}
                disabled={currentPage === totalPages}
              >
                Next
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{transform: 'rotate(180deg)'}}>
                  <path d="M15.41 7.41L14 6L8 12L14 18L15.41 16.59L10.83 12L15.41 7.41Z" fill="currentColor"/>
                </svg>
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export const LocationsGridView = ({ 
  employees, 
  timeFilter, 
  setTimeFilter, 
  onSelectLocation,
  searchQuery,
  setSearchQuery
}:LocationsGridViewProps) => {
  // Get counts of visitors for each location
  const locationCounts = getLocationVisitCounts(employees, timeFilter);
  
  // Filter function for location search
  const filteredUsers = searchQuery.trim() !== '' 
    ? employees.filter((emp:EmployeeSignIns )=> {
        // Check if user name matches search query
        if (!emp.name.toLowerCase().includes(searchQuery.toLowerCase())) {
          return false;
        }
        
        // Check if user has visited any location based on time filter
        const now = new Date();
        let startDate = new Date(now);
        
        if (timeFilter === 'day') {
          startDate.setHours(0, 0, 0, 0);
        } else if (timeFilter === 'week') {
          startDate.setDate(now.getDate() - now.getDay());
          startDate.setHours(0, 0, 0, 0);
        } else if (timeFilter === 'month') {
          startDate.setDate(1);
          startDate.setHours(0, 0, 0, 0);
        } else if (timeFilter === 'year') {
          startDate.setMonth(0, 1);
          startDate.setHours(0, 0, 0, 0);
        }
        
        const endDate = new Date(now);
        
        // Check if user has any visit during the time period
        return emp.signIns.some((s:EventData) => 
          (s.eventDate ) >= startDate && 
        (s.eventDate ) <= endDate && 
          !s.isMainOffice
        );
      }) 
    : [];
    
  // Get time period text
  const getTimePeriodText = () => {
    switch(timeFilter) {
      case 'day': return 'Today';
      case 'week': return 'This Week';
      case 'month': return 'This Month';
      case 'year': return 'This Year';
      default: return '';
    };
  };
  
  return (
    <div>
      <div className="locations-header">
        <h1 className="locations-title">Office Locations</h1>
        <div className="date-filters-apple">
          <button 
            className={`date-filter-apple ${timeFilter === 'day' ? 'active' : ''}`}
            onClick={() => setTimeFilter('day')}
          >
            Day
          </button>
          <button 
            className={`date-filter-apple ${timeFilter === 'week' ? 'active' : ''}`}
            onClick={() => setTimeFilter('week')}
          >
            Week
          </button>
          <button 
            className={`date-filter-apple ${timeFilter === 'month' ? 'active' : ''}`}
            onClick={() => setTimeFilter('month')}
          >
            Month
          </button>
          <button 
            className={`date-filter-apple ${timeFilter === 'year' ? 'active' : ''}`}
            onClick={() => setTimeFilter('year')}
          >
            Year
          </button>
        </div>
      </div>
      
      <div className="locations-search-container">
        <input
          type="text"
          className="search-bar"
          placeholder="Search employees who visited offices..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        
        {searchQuery.trim() !== '' && (
          <div className="search-results">
            {filteredUsers.length === 0 ? (
              <p>No employees found for "{searchQuery}" in {getTimePeriodText().toLowerCase()}.</p>
            ) : (
              <>
                <h3 style={{ marginBottom: '10px' }}>Users who visited offices {getTimePeriodText().toLowerCase()}:</h3>
                {filteredUsers.map((user, index) => {
                  // Get count of locations visited by this user
                  const locationsVisited = new Set();
                  const now = new Date();
                  let startDate = new Date(now);
                  
                  if (timeFilter === 'day') {
                    startDate.setHours(0, 0, 0, 0);
                  } else if (timeFilter === 'week') {
                    startDate.setDate(now.getDate() - now.getDay());
                    startDate.setHours(0, 0, 0, 0);
                  } else if (timeFilter === 'month') {
                    startDate.setDate(1);
                    startDate.setHours(0, 0, 0, 0);
                  } else if (timeFilter === 'year') {
                    startDate.setMonth(0, 1);
                    startDate.setHours(0, 0, 0, 0);
                  }
                  
                  user.signIns.forEach((s:EventData) => {
                    const eventDate = s.eventDate;
                    if (eventDate && eventDate >= startDate && 
                        eventDate <= now && 
                        !s.isMainOffice &&
                        s.location) {
                      locationsVisited.add(s.location);
                    }
                  });
                  
                  return (
                    <div key={user.id} className="result-item">
                      <div
                        className="employee-initial"
                        style={{ backgroundColor: COLOR_PALETTE[index % COLOR_PALETTE.length].initial }}
                      >
                        {user.name.charAt(0)}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div className="employee-name">{user.name}</div>
                        <div style={{ fontSize: '13px', color: 'var(--apple-text-secondary)' }}>
                          Visited {locationsVisited.size} location{locationsVisited.size !== 1 ? 's' : ''} {getTimePeriodText().toLowerCase()}
                        </div>
                      </div>
                      <div className="visit-percentage">
                        {locationsVisited.size} / {availableLocations.length}
                      </div>
                    </div>
                  );
                })}
              </>
            )}
          </div>
        )}
      </div>
      
      <div className="locations-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '20px' }}>
        {availableLocations.map((location:any, index:number) => {
          // Calculate if this is the last item and needs special styling
          const isLastItem = index === availableLocations.length - 1;
          const isLastRowWithOneItem = availableLocations.length % 3 === 1 && isLastItem;
          
          // Special styling for centered last item
          const centerStyle = isLastRowWithOneItem ? { 
            gridColumn: '2', 
            justifySelf: 'center'
          } : {};
          
          return (
            <div 
              key={location} 
              className="location-tile"
              style={centerStyle}
              onClick={() => onSelectLocation(location)}
            >
              <div className="location-emoji">
                {LOCATION_ICONS[location]}
              </div>
              <div className="location-title">{location}</div>
              <div className="location-count">
                {locationCounts[location]} visits
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

interface MedalItemProps {
    employee: any; // Replace with more specific type if available
    medalClass: string;
    medalEmoji: string;
    category: string;
    viewUserProfile: (employee: any) => void; // Replace with more specific type if available
}
const MedalItem = ({ employee, medalClass, medalEmoji, category, viewUserProfile }:MedalItemProps) => {
  const initials = (employee.name.charAt(0) + (employee.name.split(' ')[1]?.charAt(0)||'')).toUpperCase();
  return (
    <div className={`medal-item ${medalClass}`} onClick={()=>viewUserProfile(employee.id)}>
      <div className="medal-circle">
        <div className="medal-label">{medalEmoji}</div>
        {initials}
      </div>
      <div className="medal-name">{employee.name}</div>
      <div className="medal-stat">
        {category==='visits'
          ? `${employee.value} visits`
          : formatDuration(employee.value)}
      </div>
    </div>
  );
};

interface RankItemProps {
  employee: any; // Replace with more specific type if available
  rank: number;
  category: string;
  viewUserProfile: (employee: any) => void; // Replace with more specific type if available
}
const RankItem = ({ employee, rank, category, viewUserProfile }:RankItemProps) => {
  const initials = (employee.name.charAt(0) + (employee.name.split(' ')[1]?.charAt(0)||'')).toUpperCase();
  return (
    <div className="rank-item" onClick={()=>viewUserProfile(employee.id)}>
      <div className="rank-info">
        <div className="rank-badge">{rank}</div>
        <div className="rank-initial">{initials}</div>
        <div className="rank-name">{employee.name}</div>
      </div>
      <div className="rank-value">
        {category==='visits'
          ? `${employee.value} visits`
          : formatDuration(employee.value)}
      </div>
    </div>
  );
};

interface LeaderboardViewProps {
  employees: any[]; // Replace with more specific type if available
  category: string;
  setCategory: (category: string) => void;
  leaderboardMonth: number | null;
  setLeaderboardMonth: React.Dispatch<React.SetStateAction<number|null>>;
  leaderboardYear: number | null;
  setLeaderboardYear: React.Dispatch<React.SetStateAction<number|null>>;
  viewUserProfile: (employee: any) => void; // Replace with more specific type if available
}

export const LeaderboardView = ({
      employees,
      category,
      setCategory,
      leaderboardMonth,
      setLeaderboardMonth,
      leaderboardYear,
      setLeaderboardYear,
      viewUserProfile
    }:LeaderboardViewProps) => {
      const [currentPage, setCurrentPage] = React.useState(0);
      const PAGE_SIZE = 7;

      const monthOptions = [
        "January","February","March","April","May","June",
        "July","August","September","October","November","December"
      ];
      const nowYear = new Date().getFullYear();
      const yearOptions = [];
      for (let y=nowYear-5; y<=nowYear+5; y++){
        yearOptions.push(y);
      }

      const data = getLeaderboardData(employees, leaderboardMonth ?? -1, leaderboardYear ?? -1, category);
      const topThree = data.slice(0,3);
      const rest = data.slice(3);
      const totalPages = Math.ceil(rest.length / PAGE_SIZE);
      const startIndex = currentPage*PAGE_SIZE;
      const endIndex = startIndex+PAGE_SIZE;
      const visibleRanks = rest.slice(startIndex,endIndex);

      // Title text
      let titleText = "Leaderboard - ";
      if (leaderboardMonth!=null && leaderboardYear!=null) {
        titleText += monthOptions[leaderboardMonth] + " " + leaderboardYear;
      } else if (leaderboardMonth==null && leaderboardYear==null) {
        titleText += "All Time";
      } else if (leaderboardMonth!=null && leaderboardYear==null) {
        titleText += monthOptions[leaderboardMonth];
      } else if (leaderboardMonth==null && leaderboardYear!=null) {
        titleText += leaderboardYear;
      }

      const nextPage = () => {
        setCurrentPage(prev=> (prev<totalPages-1? prev+1: prev));
      };
      const prevPage = () => {
        setCurrentPage(prev=> (prev>0? prev-1: prev));
      };

      return (
        <div className="leaderboard-container">
          <div className="leaderboard-header">
            <h2 className="leaderboard-title">{titleText}</h2>
          </div>
          <div className="leaderboard-tabs">
            <div
              className={`leaderboard-tab ${category==='visits'?'active':''}`}
              onClick={()=>{setCategory('visits'); setCurrentPage(0);}}
            >
              Most Office Visits
            </div>
            <div
              className={`leaderboard-tab ${category==='duration'?'active':''}`}
              onClick={()=>{setCategory('duration'); setCurrentPage(0);}}
            >
              Longest Time Out
            </div>
          </div>
          {/* Month/Year + "All Time" button */}
          <div className="leaderboard-filter-row">
            <div className="leaderboard-dropdowns">
              <select
                value={leaderboardMonth??''}
                onChange={e=>{
                  const val = parseInt(e.target.value);
                  setLeaderboardMonth(isNaN(val)?null:val);
                  setCurrentPage(0);
                }}
              >
                <option value="">Any Month</option>
                {monthOptions.map((m,i)=><option key={i} value={i}>{m}</option>)}
              </select>
              <select
                value={leaderboardYear??''}
                onChange={e=>{
                  const val = parseInt(e.target.value);
                  setLeaderboardYear(isNaN(val)?null:val);
                  setCurrentPage(0);
                }}
              >
                <option value="">Any Year</option>
                {yearOptions.map((y,i)=><option key={i} value={y}>{y}</option>)}
              </select>
            </div>
            <button
              className={`all-time-button ${leaderboardMonth==null && leaderboardYear==null ? 'active' : ''}`}
              onClick={() => {
                setLeaderboardMonth(null);
                setLeaderboardYear(null);
                setCurrentPage(0);
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M11.99 2C6.47 2 2 6.48 2 12C2 17.52 6.47 22 11.99 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 11.99 2ZM12 20C7.58 20 4 16.41 4 12C4 7.58 7.58 4 12 4C16.41 4 20 7.59 20 12C20 16.41 16.41 20 12 20Z" fill="currentColor"/>
              </svg>
              <span>All Time</span>
            </button>
          </div>
          {data.length===0 ? (
            <p className="no-data">No data available for this selection.</p>
          ) : (
            <>
              <div className="top-three-container">
                {topThree[1] && (
                  <MedalItem
                    employee={topThree[1]}
                    medalClass="silver-medal"
                    medalEmoji="🥈"
                    category={category}
                    viewUserProfile={viewUserProfile}
                  />
                )}
                {topThree[0] && (
                  <MedalItem
                    employee={topThree[0]}
                    medalClass="gold-medal"
                    medalEmoji="🥇"
                    category={category}
                    viewUserProfile={viewUserProfile}
                  />
                )}
                {topThree[2] && (
                  <MedalItem
                    employee={topThree[2]}
                    medalClass="bronze-medal"
                    medalEmoji="🥉"
                    category={category}
                    viewUserProfile={viewUserProfile}
                  />
                )}
              </div>
              {rest.length>0 && (
                <>
                  <div className="pagination-controls">
                    <button
                      className="page-button"
                      onClick={prevPage}
                      disabled={currentPage===0}
                    >
                      « Prev
                    </button>
                    <span>Page {currentPage+1} of {totalPages}</span>
                    <button
                      className="page-button"
                      onClick={nextPage}
                      disabled={currentPage===totalPages-1}
                    >
                      Next »
                    </button>
                  </div>
                  <div className="rank-list">
                    {visibleRanks.map((emp:any,i:any)=>(
                      <RankItem
                        key={emp.id}
                        employee={emp}
                        rank={i+4+(currentPage*PAGE_SIZE)}
                        category={category}
                        viewUserProfile={viewUserProfile}
                      />
                    ))}
                  </div>
                </>
              )}
            </>
          )}
        </div>
      );
    };


